import React, { useCallback, useEffect, useRef, useState } from "react";
import { FULLSCREEN_MODES } from "../../../constants/common";
import { useScreenOrientation } from "../../../hooks/useScreenOrientation";
import { useLocaleContext } from "../../../context/LocaleContext";
import ProgressBar from "../ProgressBar";
import PlayPauseButton from "./PlayPauseButton";
import VolumeLevelControl from "./VolumeLevelControl";
import PlayerTimeControl from "./PlayerTimeControl";
import LiveButton from "./LiveButton";
import FullScreenSideModuleButton from "./FullScreenSideModuleButton";
import FullScreenButton from "./FullScreenButton";
import VolumeButton from "./VolumeButton";
import { ComdiLogo } from "../../Icons/IconPackage/ComdiLogo";
import {
    SettingsButton,
    SettingsGlobalButton
} from "./Settings";
import { SettingsIcon } from "../Icons";
import ProgressBarHint from "../ProgressBarHint";
import MiniPlayerButton from "./MiniPlayerButton";
import { LIVE_OFFSET } from "../consts";
import ReactionsButton from "./ReactionsButton";
import "./style.scss";

export const Spacer = () => (
    <div className="stream-player-controls-center" />
)

export const ControlsContainer = ({ isFullScreen, renderProgressBar, children, isLandscape }) => (
    <aside className={`stream-player-controls ${isFullScreen ? "controls-bottom" : ""} ${!renderProgressBar ? "no-progress-bar" : ""} ${isLandscape ? '-landscape' : ''}`}>{children}</aside>
)
export const ControlsToolsContainer = ({ children }) => (
    <div className="stream-player-controls-tools">{children}</div>
)

const getQualitySup = (quality) => {
    if (quality.height >= 1000) {
        return 'FullHD';
    }
    if (quality.height >= 700) {
        return 'HD';
    }
    return !!quality.height ? 'SD' : '';
}

export default (
    {
        playerRef,
        controlsHidden,
        paused,
        isMobile,
        episode,
        currentTime,
        embedOffset,
        streamDay,
        duration,
        qualities,
        quality,
        languages,
        language,
        stream,
        streamEpisodes,
        hasDvr = false,
        hasLive = false,
        isLive = false,
        brandingEnabled = true,
        onNavigate,
        onQualityChange,
        onLanguageChange,
        onLiveButtonClick,
        tabletView,
        fullscreenButtonEnabled,
        fullScreenSideModuleButtonEnabled,
        fullScreen,
        onFullScreenClick,
        mountMiniPlayer,
        isShowReactions,
        onToggleReactions,
        reactionsActive,
    }) => {
    const HINT_VISIBLE_TIME = 5;
    const playbackRate = useRef(1.0);
    const { get: getLocale } = useLocaleContext();
    const [controlsDisabled, setControlsDisabled] = useState(controlsHidden);
    const [video, setVideo] = useState(null);
    // const [paused, setPaused] = useState(true);
    const [volume, setVolume] = useState(0);
    const [muted, setMuted] = useState(false);
    const [hintPosition, setHintPosition] = useState(null);
    const sliderRef = useRef(null);
    const controlsHiddenTimeoutRef = useRef(null);
    const hintPositionResetTimerRef = useRef(null);
    const timeOffset = stream.archiveStatus === 'file' && stream.offset ? stream.offset : 0;
    const { isOrientationLandscape } = useScreenOrientation();

    const playbackRateOptions = [
        // { value: 0.25, label: 'x0.25' },
        // { value: 0.5, label: 'x0.5' },
        { value: 0.75, label: 'x0.75' },
        { value: 1.0, label: 'x1' },
        // { value: 1.25, label: 'x1.25' },
        { value: 1.5, label: 'x1.5' },
        // { value: 1.75, label: 'x1.75' },
        { value: 2.0, label: 'x2' },
    ];

    const handlePlay = () => {
        if (!video) return;
        try {
          video.play()
        } catch (e) {
            console.info(e)
        }
    }

    const handlePause = () => {
        if (!video) return;
        try {
          video.pause()
        } catch (e) {
            console.info(e)
        }
    }

    const handleQualityChange = (number) => {
        onQualityChange(qualities.find((quality) => quality.number === number));
    }
    const handleLanguageChange = (channel) => {
        onLanguageChange(languages.find((language) => language.channel === channel));
    }
    const handleMiniPlayer = () => {
        handlePlay();
        mountMiniPlayer();
    }
    const handleDVR = useCallback((video) => {
        if (video && !hasDvr && hasLive) {
            onNavigate((Number.isFinite(video.duration) ? video.duration : duration) - LIVE_OFFSET)
        }
    }, [hasDvr, hasLive, duration])

    const handlePlaybackRate = (rate) => {
        if (!video) return;
        const _rate = parseFloat(rate);
        video.playbackRate = _rate;
        playbackRate.current = _rate;
    }

    useEffect(() => {
        const messageHandler = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                if (data?.hasOwnProperty('mute')) {
                    setMuted(data?.mute);
                }

                if (data?.hasOwnProperty('volume')) {
                    !!data?.volume && setMuted(false);
                    setVolume(data?.volume);
                }
            } catch (error) {
                console.error(error);
            }
        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        }
    }, []);

    useEffect(() => {
        if (!paused) {
            handleDVR(video)
        }
    }, [paused])

    useEffect(() => {
        if (!video) {
            return;
        }

        // video.addEventListener('play', () => setPaused(false));
        // video.addEventListener('pause', () => setPaused(true));
        // video.addEventListener('timeupdate', () => setCurrentTime(video.currentTime));
        // video.addEventListener('durationchange', () => setDuration(video.duration));

        video.addEventListener('loadeddata', () => {
            if (video.playbackRate !== playbackRate.current) {
                video.playbackRate = playbackRate.current
            }
        });
        // video.addEventListener('ratechange', () => setPlaybackRate(video.playbackRate));
        video.addEventListener('volumechange', () => {
            setVolume(video.volume);
            setMuted(video.muted);
        });
        setVolume(video.volume);
        setMuted(video.muted);

        const keyDownListener = (e) => {
            let tagName = (e.target.role || e.target.tagName).toLowerCase();
            if (!video || ['input', 'textarea', 'select'].includes(tagName)) {
                return;
            }
            switch (e.code) {
                case 'ArrowRight':
                    onNavigate(Math.min(video.duration - embedOffset, video.currentTime + LIVE_OFFSET - embedOffset + 10))
                    break;
                case 'ArrowLeft': onNavigate(video.currentTime - embedOffset - 10); break;
                case 'Space': video.paused ? handlePlay() : handlePause(); break;
                case 'ArrowUp':
                    setVolume((prev) => (prev < 1 ? prev + 0.1 : prev));
                    setMuted(false);
                    break;
                case 'ArrowDown':
                    setVolume((prev) => (prev > 0 ? prev - 0.1 : prev));
                    setMuted(false);
                    break;
                default: return;
            }
            e.preventDefault();
        };

        window.addEventListener('keydown', keyDownListener);

        return () => {
            window.removeEventListener('keydown', keyDownListener);
        }
    }, [video]);

    useEffect(() => {
        setVideo(playerRef.current);
    }, [playerRef.current]);

    useEffect(() => {
        if (video) {
            handlePlaybackRate(1.0)
        }
    }, [isLive]);

    useEffect(() => {
        hintPositionResetTimerRef.current = setTimeout(() => {
            setHintPosition(null);
        }, HINT_VISIBLE_TIME * 1000);

        return () => {
            clearTimeout(hintPositionResetTimerRef.current);
        }
    }, [hintPosition]);

    useEffect(() => {
        controlsHiddenTimeoutRef.current = setTimeout(() => setControlsDisabled(controlsHidden), 300);
        return () => clearTimeout(controlsHiddenTimeoutRef.current);
    }, [controlsHidden])

    if (!video) {
        return null;
    }

    const disabledMiniPlayer = tabletView && !video.currentTime;
    const isLandscape = tabletView && fullScreen === FULLSCREEN_MODES.VIDEO && isOrientationLandscape;

    return (
        <>
            {!episode && <ProgressBarHint
                position={hintPosition}
                duration={duration}
                width={sliderRef.current?.offsetWidth}
                stream={stream}
                streamEpisodes={streamEpisodes}
                streamDay={streamDay}
                offsetLeft={20}
                timeOffset={timeOffset}
            />}
            <ControlsContainer isFullScreen={fullScreen === FULLSCREEN_MODES.VIDEO} renderProgressBar={hasDvr} isLandscape={isLandscape} >
                {hasDvr && <ProgressBar
                    currentTime={currentTime}
                    duration={duration}
                    isLive={isLive}
                    stream={stream}
                    streamDay={streamDay}
                    streamEpisodes={streamEpisodes}
                    onChange={onNavigate}
                    onHintPositionChange={setHintPosition}
                    sliderRef={sliderRef}
                    timeOffset={timeOffset}
                    episode={episode}
                />}
                <ControlsToolsContainer>
                    <PlayPauseButton
                        paused={paused}
                        onPlay={handlePlay}
                        onPause={handlePause}
                    />
                    <VolumeButton
                        muted={muted}
                        volume={volume}
                        onChange={() => video.muted = !video.muted}
                    />
                    {!tabletView && <VolumeLevelControl
                        muted={muted}
                        volume={volume}
                        onChange={(volume) => video.volume = volume}
                    />}
                    {hasDvr && <PlayerTimeControl
                        currentTime={currentTime}
                        duration={duration}
                        hasDvr={hasDvr}
                        hasLive={hasLive}
                        isLive={isLive}
                    />}
                    <LiveButton
                        visible={hasLive && onLiveButtonClick}
                        active={isLive || !hasDvr}
                        onClick={onLiveButtonClick}
                    />

                    <Spacer />

                    <SettingsGlobalButton
                        playbackRates={isLive || !hasDvr ? null : playbackRateOptions}
                        playbackRate={playbackRate.current}
                        onChangePlaybackRate={handlePlaybackRate}
                        qualities={qualities.map((quality) => ({ value: quality.number, label: quality.label, sup: getQualitySup(quality) }))}
                        quality={quality}
                        onChangeQuality={(value) => handleQualityChange(parseInt(value))}
                        languages={languages.map(({ channel: value, description: label }) => ({ value, label }))}
                        language={language}
                        onChangeLanguage={(value) => handleLanguageChange(value)}
                    ><SettingsIcon /></SettingsGlobalButton>

                    {!isLive && (
                        <SettingsButton
                            title={getLocale('broadcast.player.playbackRate.label')}
                            value={playbackRate.current}
                            options={playbackRateOptions}
                            onChange={handlePlaybackRate}
                        >{playbackRateOptions.find(({ value }) => value === playbackRate.current)?.label || playbackRate.current}</SettingsButton>
                    )}
                    {languages.length > 0 && (
                        <SettingsButton
                            title={getLocale('broadcast.player.language.label')}
                            value={language?.channel}
                            options={languages.map(({ channel: value, description: label }) => ({ value, label }))}
                            onChange={(value) => handleLanguageChange(value)}
                        >{language?.description}</SettingsButton>
                    )}
                    {qualities.length > 0 && (
                        <SettingsButton
                            title={getLocale('broadcast.player.quality.label')}
                            value={quality?.number}
                            options={qualities.map((quality) => ({ value: quality.number, label: quality.label, sup: getQualitySup(quality) }))}
                            onChange={(value) => handleQualityChange(parseInt(value))}
                        >{quality?.label} <sup className={'quality-label-sup'}>{getQualitySup(quality)}</sup></SettingsButton>
                    )}

                    <MiniPlayerButton onClick={handleMiniPlayer} disabled={disabledMiniPlayer} />

                    {fullScreenSideModuleButtonEnabled && (
                        <FullScreenSideModuleButton onClick={() => onFullScreenClick(FULLSCREEN_MODES.CHAT)} active={fullScreen === FULLSCREEN_MODES.CHAT} />
                    )}
                    {fullscreenButtonEnabled && (
                        <FullScreenButton onClick={() => onFullScreenClick(FULLSCREEN_MODES.VIDEO)} active={fullScreen === FULLSCREEN_MODES.VIDEO} />
                    )}
                    {isShowReactions && <ReactionsButton onClick={onToggleReactions} active={reactionsActive} />}
                    {brandingEnabled && (
                        <a className={`stream-player-controls-brand`} href={`https://www.comdi.com/?utm_source=platform&utm_medium=logo_player&utm_campaign=users`} target="_blank">
                            <ComdiLogo />
                        </a>
                    )}
                </ControlsToolsContainer>
                {(isMobile && controlsDisabled) && <div className="stream-player-controls-disabled-layer" />}
            </ControlsContainer>
        </>
    );
}
